import React, { useState, useEffect } from 'react';
import { QueryClient, QueryClientProvider, useQuery } from 'react-query';

interface VariantData {
  id: number;
  available: boolean;
  price: number;
  compare_at_price: number | null;
}

interface BuyNowButtonProps {
  ctx: any;
  el: HTMLElement;
  [key: string]: any;
}

const BuyNowButton = (props: BuyNowButtonProps) => {
  const [currentVariant, setCurrentVariant] = useState<VariantData | null>(null);
  const [isAvailable, setIsAvailable] = useState<boolean>(false);
  const [variantId, setVariantId] = useState<string | null>(null);
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  // Fetch variant data
  const { data: variantData, isLoading } = useQuery(
    ['variant', variantId],
    async () => {
      if (!variantId) return null;
      const response = await fetch(`/variants/${variantId}.js`);
      return response.json();
    },
    {
      enabled: !!variantId,
      onSuccess: (data) => {
        if (data) {
          setCurrentVariant({
            id: data.id,
            available: data.available,
            price: data.price,
            compare_at_price: data.compare_at_price
          });
          setIsAvailable(data.available);
        }
      }
    }
  );

  const handleBuyNow = async () => {
    if (!variantId || !isAvailable) return;

    try {
      setIsAddingToCart(true);
      // Redirect to Shopify's add-to-cart-and-checkout URL
      window.location.href = `/cart/${variantId}:1`;
    } catch (error) {
      console.error('Buy now error:', error);
      setIsAddingToCart(false);
    }
  };

  useEffect(() => {
    const form = props.el.closest('form');
    
    // Get the initial variant data
    const getInitialVariant = () => {
      const variantInput = form?.querySelector('input[name="id"]');
      const newVariantId = variantInput?.getAttribute('value');
      if (newVariantId) {
        setVariantId(newVariantId);
      }
    };

    // Handle variant changes
    const handleVariantChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      const variant = customEvent.detail.variant;
      console.log('variant: ', variant);
      
      if (variant) {
        setCurrentVariant({
          id: variant.id,
          available: variant.available,
          price: variant.price,
          compare_at_price: variant.compare_at_price
        });
        setIsAvailable(variant.available);
        setVariantId(variant.id.toString());
      }
    };

    // Initialize
    getInitialVariant();

    // Set up event listener
    if (form) {
      form.addEventListener('variant:change', handleVariantChange);
    }

    // Cleanup
    return () => {
      if (form) {
        form.removeEventListener('variant:change', handleVariantChange);
      }
    };
  }, [props.el]);

  return (
    <div>
      <button 
        type="button" 
        className={`button w-full ${!isAvailable ? 'button--disabled' : ''}`}
        disabled={isLoading || isAddingToCart || !isAvailable}
        onClick={handleBuyNow}
      >
        {isLoading ? (
          <span className="loading-text">
            <span className="loading-dots">Loading</span>
          </span>
        ) : isAddingToCart ? (
          <span className="loading-text">
            <span className="loading-dots">Processing</span>
          </span>
        ) : isAvailable ? (
          'Buy Now'
        ) : (
          'Sold out'
        )}
      </button>
    </div>
  );
};

// Create a wrapper component with QueryClientProvider
const BuyNowButtonProvider = (props: BuyNowButtonProps) => {
  const queryClient = new QueryClient();

  return (
    <QueryClientProvider client={queryClient}>
      <BuyNowButton {...props} />
    </QueryClientProvider>
  );
};

export default BuyNowButtonProvider;